import { Injectable } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  combineLatest,
  forkJoin,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  tap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiEndpointsService } from './api-endpoints.service';
import { ApiHttpService } from './api-http.service';

export interface AceNotificationData {
  body?: string | null;
  deepLink?: string | null;
  url?: string | null;
  route?: string | null;
  fundId?: string | null;
  taskId?: string | null;
  requestId?: string | null;
  eventId?: string | null;
  channelId?: string | null;
  [key: string]: unknown;
}

export interface AceNotification {
  id: number;
  read: boolean;
  sender: unknown | null;
  title: string;
  type: string;
  data: AceNotificationData;
  audit: {
    createdDate: number;
    modifiedDate: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AceNotificationService {
  private static readonly pageSize = 20;

  private readonly notificationsSubject = new BehaviorSubject<
    AceNotification[]
  >([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly unreadNotificationCountSubject = new BehaviorSubject<number>(
    0
  );
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly loadingMoreSubject = new BehaviorSubject<boolean>(false);
  private readonly hasMoreSubject = new BehaviorSubject<boolean>(true);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private readonly loadMoreErrorSubject = new BehaviorSubject<string | null>(
    null
  );
  private readonly markAllLoadingSubject = new BehaviorSubject<boolean>(false);
  private refreshRequest = 0;
  private loadMoreRequest = 0;
  private mutationVersion = 0;
  private currentPage = 0;

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly unreadNotificationCount$ =
    this.unreadNotificationCountSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly loadingMore$ = this.loadingMoreSubject.asObservable();
  readonly hasMore$ = this.hasMoreSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();
  readonly loadMoreError$ = this.loadMoreErrorSubject.asObservable();
  readonly markAllLoading$ = this.markAllLoadingSubject.asObservable();
  readonly canMarkAllAsRead$ = combineLatest([
    this.unreadNotificationCount$,
    this.notifications$,
  ]).pipe(
    map(
      ([unreadCount, notifications]) =>
        unreadCount > 0 ||
        notifications.some(notification => !notification.read)
    ),
    distinctUntilChanged()
  );

  constructor(
    private readonly apiHttpService: ApiHttpService,
    private readonly apiEndpointsService: ApiEndpointsService
  ) {}

  refresh(): Observable<void> {
    if (!environment.teamApiUrl?.trim()) {
      this.refreshRequest++;
      this.loadMoreRequest++;
      this.currentPage = 0;
      this.loadingMoreSubject.next(false);
      this.notificationsSubject.next([]);
      this.unreadCountSubject.next(0);
      this.unreadNotificationCountSubject.next(0);
      this.hasMoreSubject.next(false);
      this.errorSubject.next(null);
      this.loadMoreErrorSubject.next(null);
      return of(void 0);
    }

    const request = ++this.refreshRequest;
    this.loadMoreRequest++;
    const mutation = this.mutationVersion;
    this.currentPage = 0;
    this.loadingMoreSubject.next(false);
    this.hasMoreSubject.next(true);
    this.loadingSubject.next(true);
    this.errorSubject.next(null);
    this.loadMoreErrorSubject.next(null);

    let listFailed = false;
    let countFailed = false;
    let notificationCountFailed = false;
    const list$ = this.apiHttpService
      .get(
        this.apiEndpointsService.getNotifications({
          page: 0,
          size: AceNotificationService.pageSize,
        })
      )
      .pipe(
        map(response => this.unwrapNotificationsPage(response)),
        catchError(() => {
          listFailed = true;
          return of({ items: [] as AceNotification[], hasMore: false });
        })
      );
    const count$ = this.apiHttpService
      .get(this.apiEndpointsService.getNotificationBadgeCount())
      .pipe(
        map(response => this.unwrapCount(response)),
        catchError(() => {
          countFailed = true;
          return of(null);
        })
      );
    const notificationCount$ = this.apiHttpService
      .get(this.apiEndpointsService.getNotificationUnreadCount())
      .pipe(
        map(response => this.unwrapCount(response)),
        catchError(() => {
          notificationCountFailed = true;
          return of(null);
        })
      );

    return forkJoin({
      notifications: list$,
      unreadCount: count$,
      unreadNotificationCount: notificationCount$,
    }).pipe(
      tap(result => {
        if (
          request !== this.refreshRequest ||
          mutation !== this.mutationVersion
        ) {
          return;
        }
        this.notificationsSubject.next(result.notifications.items);
        this.hasMoreSubject.next(!listFailed && result.notifications.hasMore);
        if (result.unreadCount !== null) {
          this.unreadCountSubject.next(result.unreadCount);
        }
        if (result.unreadNotificationCount !== null) {
          this.unreadNotificationCountSubject.next(
            result.unreadNotificationCount
          );
        }
        this.errorSubject.next(
          listFailed || countFailed || notificationCountFailed
            ? 'Notifications could not be fully refreshed.'
            : null
        );
      }),
      map(() => void 0),
      finalize(() => {
        if (request === this.refreshRequest) {
          this.loadingSubject.next(false);
        }
      })
    );
  }

  loadMore(): Observable<void> {
    if (
      !environment.teamApiUrl?.trim() ||
      !this.hasMoreSubject.value ||
      this.loadingSubject.value ||
      this.loadingMoreSubject.value
    ) {
      return of(void 0);
    }

    const request = ++this.loadMoreRequest;
    const refreshRequest = this.refreshRequest;
    const mutation = this.mutationVersion;
    const page = this.currentPage + 1;
    this.loadingMoreSubject.next(true);
    this.loadMoreErrorSubject.next(null);

    return this.apiHttpService
      .get(
        this.apiEndpointsService.getNotifications({
          page,
          size: AceNotificationService.pageSize,
        })
      )
      .pipe(
        map(response => this.unwrapNotificationsPage(response)),
        tap(result => {
          if (
            request !== this.loadMoreRequest ||
            refreshRequest !== this.refreshRequest ||
            mutation !== this.mutationVersion
          ) {
            return;
          }

          const current = this.notificationsSubject.value;
          const existingIds = new Set(current.map(item => item.id));
          const newItems = result.items.filter(
            item => !existingIds.has(item.id)
          );
          this.notificationsSubject.next([...current, ...newItems]);
          this.currentPage = page;
          this.hasMoreSubject.next(result.hasMore && newItems.length > 0);
        }),
        catchError(error => {
          if (
            request === this.loadMoreRequest &&
            refreshRequest === this.refreshRequest
          ) {
            this.loadMoreErrorSubject.next(
              'More notifications could not be loaded.'
            );
          }
          return throwError(() => error);
        }),
        map(() => void 0),
        finalize(() => {
          if (request === this.loadMoreRequest) {
            this.loadingMoreSubject.next(false);
          }
        })
      );
  }

  markAsRead(id: number): Observable<void> {
    return this.apiHttpService
      .patch(this.apiEndpointsService.markNotificationRead(id), {})
      .pipe(
        tap(() => {
          this.mutationVersion++;
          const current = this.notificationsSubject.value;
          const notification = current.find(item => item.id === id);
          if (!notification || notification.read) return;
          this.notificationsSubject.next(
            current.map(item =>
              item.id === id ? { ...item, read: true } : item
            )
          );
          this.unreadCountSubject.next(
            Math.max(0, this.unreadCountSubject.value - 1)
          );
          this.unreadNotificationCountSubject.next(
            Math.max(0, this.unreadNotificationCountSubject.value - 1)
          );
        }),
        map(() => void 0),
        catchError(error => {
          this.errorSubject.next('Notification could not be marked as read.');
          return throwError(() => error);
        })
      );
  }

  markAllAsRead(): Observable<void> {
    if (this.markAllLoadingSubject.value) return of(void 0);

    this.markAllLoadingSubject.next(true);
    return this.apiHttpService
      .patch(this.apiEndpointsService.markAllNotificationsRead(), {})
      .pipe(
        tap(() => {
          this.mutationVersion++;
          this.notificationsSubject.next(
            this.notificationsSubject.value.map(item => ({
              ...item,
              read: true,
            }))
          );
          this.unreadCountSubject.next(0);
          this.unreadNotificationCountSubject.next(0);
        }),
        map(() => void 0),
        catchError(error => {
          this.errorSubject.next('Notifications could not be marked as read.');
          return throwError(() => error);
        }),
        finalize(() => this.markAllLoadingSubject.next(false))
      );
  }

  private unwrapNotificationsPage(response: unknown): {
    items: AceNotification[];
    hasMore: boolean;
  } {
    if (Array.isArray(response)) {
      const items = response
        .map(item => this.toNotification(item))
        .filter(item => item.id > 0);
      return {
        items,
        hasMore: items.length >= AceNotificationService.pageSize,
      };
    }
    if (!response || typeof response !== 'object') {
      return { items: [], hasMore: false };
    }
    const envelope = response as { data?: unknown; items?: unknown };
    const value = Array.isArray(envelope.data) ? envelope.data : envelope.items;
    const items = Array.isArray(value)
      ? value.map(item => this.toNotification(item)).filter(item => item.id > 0)
      : [];
    const metadata = response as {
      hasMore?: unknown;
      totalPages?: unknown;
      page?: unknown;
      size?: unknown;
    };
    const hasMore =
      typeof metadata.hasMore === 'boolean'
        ? metadata.hasMore
        : typeof metadata.totalPages === 'number' &&
          typeof metadata.page === 'number'
        ? metadata.page + 1 < metadata.totalPages
        : items.length >= AceNotificationService.pageSize;
    return { items, hasMore };
  }

  private toNotification(value: unknown): AceNotification {
    const item =
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : {};
    const rawData = item['data'];
    const data =
      rawData && typeof rawData === 'object' && !Array.isArray(rawData)
        ? (rawData as AceNotificationData)
        : {};
    const rawAudit = item['audit'];
    const audit =
      rawAudit && typeof rawAudit === 'object'
        ? (rawAudit as Record<string, unknown>)
        : {};
    return {
      id: Number(item['id']) || 0,
      read: item['read'] === true,
      sender: item['sender'] ?? null,
      title: typeof item['title'] === 'string' ? item['title'] : 'Notification',
      type: typeof item['type'] === 'string' ? item['type'] : 'GENERAL',
      data,
      audit: {
        createdDate: Number(audit['createdDate']) || 0,
        modifiedDate: Number(audit['modifiedDate']) || 0,
      },
    };
  }

  private unwrapCount(response: unknown): number {
    if (typeof response === 'number') return Math.max(0, response);
    if (!response || typeof response !== 'object') return 0;
    const value = response as {
      badgeCount?: unknown;
      unreadCount?: unknown;
      data?: unknown;
    };
    if (typeof value.data === 'number') return Math.max(0, value.data);
    const count = value.badgeCount ?? value.unreadCount;
    return typeof count === 'number' && Number.isFinite(count)
      ? Math.max(0, count)
      : 0;
  }
}
